import {
  type ReactElement,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../lib/utils.js';
import { buttonVariants } from './button.js';
import {
  DIALOG_BACKDROP,
  DialogContentPrimitive,
  DialogDescriptionPrimitive,
  DialogRoot,
  DialogTitlePrimitive,
} from './dialog-primitive.js';

/**
 * `const ok = await confirm({ ... })` — one helper instead of a hand-rolled modal per screen.
 *
 * Destructive actions in this product are genuinely destructive: cancelling an order the
 * karigar has already cut fabric for, deleting a measurement profile a shop has kept for
 * years. Every one of them goes through here, and the copy is always specific — the caller
 * passes translated strings, never a generic "Are you sure?".
 *
 * It is an alert dialog on the same native `<dialog>` as `dialog.tsx`: Escape answers "no", but
 * a press on the backdrop does nothing — the question needs an answer, not a dismissal.
 */

export interface ConfirmOptions {
  title: string;
  description?: string;
  /** Defaults to the locale's generic confirm label. */
  confirmLabel?: string;
  cancelLabel?: string;
  /** Paints the confirm button red and makes Cancel the focused default. */
  destructive?: boolean;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function useConfirm(): ConfirmFn {
  const confirm = useContext(ConfirmContext);
  if (!confirm) {
    throw new Error('useConfirm must be used inside <ConfirmProvider>');
  }
  return confirm;
}

export function ConfirmProvider({ children }: { children: ReactNode }): ReactElement {
  const { t } = useTranslation();
  const [request, setRequest] = useState<ConfirmOptions | null>(null);
  const resolveRef = useRef<((value: boolean) => void) | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    setRequest(options);
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const settle = useCallback((result: boolean) => {
    resolveRef.current?.(result);
    resolveRef.current = null;
    setRequest(null);
  }, []);

  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <DialogRoot
        open={request !== null}
        onOpenChange={(open) => {
          // Escape is an answer too, and the answer is "no".
          if (!open) settle(false);
        }}
      >
        <DialogContentPrimitive
          role="alertdialog"
          className={cn(
            'fixed inset-x-0 bottom-0 top-auto z-50 m-0 flex max-h-none w-full max-w-none flex-col gap-4 rounded-t-xl border border-border bg-background p-4 text-foreground shadow-lg',
            'sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:w-full sm:max-w-md sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-xl sm:p-6',
            DIALOG_BACKDROP,
          )}
          // An alert dialog is answered, not dismissed: a stray tap beside it is not a "no".
          onPointerDownOutside={(event) => {
            event.preventDefault();
          }}
          // Cancel takes the focus, so an Enter pressed out of habit is the safe answer.
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            cancelRef.current?.focus();
          }}
        >
          <DialogTitlePrimitive className="text-lg font-semibold leading-tight">
            {request?.title ?? ''}
          </DialogTitlePrimitive>
          {request?.description ? (
            <DialogDescriptionPrimitive className="text-sm text-muted-foreground">
              {request.description}
            </DialogDescriptionPrimitive>
          ) : null}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              ref={cancelRef}
              type="button"
              className={cn(buttonVariants({ variant: 'outline' }))}
              onClick={() => {
                settle(false);
              }}
            >
              {request?.cancelLabel ?? t('common.cancel')}
            </button>
            <button
              type="button"
              className={cn(
                buttonVariants({ variant: request?.destructive ? 'destructive' : 'default' }),
              )}
              onClick={() => {
                settle(true);
              }}
            >
              {request?.confirmLabel ?? t('common.confirm')}
            </button>
          </div>
        </DialogContentPrimitive>
      </DialogRoot>
    </ConfirmContext.Provider>
  );
}
